'use client';

import { useState } from 'react';
import { Android, Apple, Desktop, ExtensionBase, Mail, Watch } from '@doist/icons/brand';
import { Container, Hidden, Image, Link } from '@doist/marketist';
import cn from 'clsx';
import { Alert } from 'components/alert/alert';
import { FeatureRadioTile } from 'components/feature-radio-tile/feature-radio-tile';
import { FeatureRadioTileGroup } from 'components/feature-radio-tile/feature-radio-tile-group';
import { IconRadioTile } from 'components/icon-radio-tile/icon-radio-tile';
import { IconRadioTileGroup } from 'components/icon-radio-tile/icon-radio-tile-group';
import Interpolate from 'components/Interpolate/Interpolate';
import { ProductUIFrameBackground } from 'components/product-ui/product-ui-frame-background/product-ui-frame-background';
import { ProductUIThemeProvider } from 'components/product-ui/product-ui-theme-context';
import { SectionHeader } from 'components/SectionHeader/SectionHeader';
import { TextLockUp } from 'components/text-lock-up/text-lock-up';
import { useTranslation } from 'next-i18next';
import { createValueTypeGuard } from 'services/create-value-type-guard';
import css from './apps-section.module.css';
import type { FeatureRadioTileProps } from 'components/feature-radio-tile/feature-radio-tile';
import type { ReactNode } from 'react';
const radioTileGroupValues = ['desktop', 'android', 'mail', 'extensions', 'wearables', 'ios'];
type IconRadioTileGroupValue = (typeof radioTileGroupValues)[number];
const typeGuard = createValueTypeGuard<IconRadioTileGroupValue>(radioTileGroupValues);
type AppSectionProps = {
  variant: FeatureRadioTileProps['variant'];
  title: string;
  subtitle: string;
  illustration?: ReactNode;
};
function localisedImages(lang: string) {
  return [{
    id: 'desktop',
    src: `/static/apps-section/${lang}/desktop.png`,
    label: 'desktop',
    icon: <Desktop />
  }, {
    id: 'android',
    src: `/static/apps-section/${lang}/android.png`,
    label: 'android',
    icon: <Android />
  }, {
    id: 'ios',
    src: `/static/apps-section/${lang}/ios.png`,
    label: 'ios',
    icon: <Apple />
  }, {
    id: 'wearables',
    src: `/static/apps-section/${lang}/wearables.png`,
    label: 'wearables',
    icon: <Watch />
  }, {
    id: 'extensions',
    src: `/static/apps-section/${lang}/browser-extensions.png`,
    label: 'browserExtensions',
    icon: <ExtensionBase />
  }, {
    id: 'mail',
    src: `/static/apps-section/${lang}/email-add-ons.png`,
    label: 'emailAddOns',
    icon: <Mail />
  }];
}
export function AppsSection({
  variant,
  title,
  subtitle,
  illustration
}: AppSectionProps) {
  const {
    t,
    i18n
  } = useTranslation('features-teams');
  const [iconGroup, setIconGroup] = useState<IconRadioTileGroupValue>('desktop');
  const images = localisedImages(i18n.language);
  function iconRadioGroupChangeHandler(value: string) {
    if (typeGuard(value)) {
      setIconGroup(value);
    }
  }
  return <ProductUIThemeProvider theme={variant} data-sentry-element="ProductUIThemeProvider" data-sentry-component="AppsSection" data-sentry-source-file="apps-section.tsx">
            <div>
                <Hidden above="md" data-sentry-element="Hidden" data-sentry-source-file="apps-section.tsx">
                    {illustration ? illustration : null}
                    <Container width="sm" className="mbe-40" data-sentry-element="Container" data-sentry-source-file="apps-section.tsx">
                        <TextLockUp size="lg" title={title} data-sentry-element="TextLockUp" data-sentry-source-file="apps-section.tsx">
                            {subtitle}
                        </TextLockUp>
                    </Container>
                    <Container width="base" data-sentry-element="Container" data-sentry-source-file="apps-section.tsx">
                        <FeatureRadioTileGroup onChange={iconRadioGroupChangeHandler} value={iconGroup} data-sentry-element="FeatureRadioTileGroup" data-sentry-source-file="apps-section.tsx">
                            {images.map(({
              id,
              icon,
              label,
              src
            }) => <FeatureRadioTile key={id} title={t(label)} icon={icon} value={id} scene={<Image key={`${id}-scene`} src={`${process.env.NEXT_PUBLIC_BASE_URL}${src}`} className={cn(css.image, {
              [css.active]: iconGroup === id
            })} width={1088} height={544} alt="" />} />)}
                        </FeatureRadioTileGroup>
                    </Container>
                </Hidden>

                <Hidden below="md" data-sentry-element="Hidden" data-sentry-source-file="apps-section.tsx">
                    <Container className={css.heading} width="sm" data-sentry-element="Container" data-sentry-source-file="apps-section.tsx">
                        {illustration ? illustration : null}
                        <SectionHeader size="lg" title={title} subtitle={subtitle} data-sentry-element="SectionHeader" data-sentry-source-file="apps-section.tsx" />
                    </Container>
                    <Container width="base" className={css.galleryContainer} data-sentry-element="Container" data-sentry-source-file="apps-section.tsx">
                        <div className={css.controlContainer}>
                            <IconRadioTileGroup value={iconGroup} onChange={iconRadioGroupChangeHandler} data-sentry-element="IconRadioTileGroup" data-sentry-source-file="apps-section.tsx">
                                {images.map(({
                id,
                icon,
                label
              }) => <IconRadioTile key={id} icon={icon} value={id}>
                                        {t(label)}
                                    </IconRadioTile>)}
                            </IconRadioTileGroup>
                        </div>
                        <div className={cn(css.imageContainer)}>
                            <ProductUIFrameBackground className={css.background} variant="wave-two" data-sentry-element="ProductUIFrameBackground" data-sentry-source-file="apps-section.tsx" />
                            {images.map(({
              id,
              src
            }) => <Image key={id} src={`${process.env.NEXT_PUBLIC_BASE_URL}${src}`} className={cn(css.image, {
              [css.active]: iconGroup === id
            })} width={1088} height={544} alt="" />)}
                        </div>
                    </Container>
                </Hidden>
            </div>
            <Container width="sm" className={cn('mbs-24', css.alertSection)} data-sentry-element="Container" data-sentry-source-file="apps-section.tsx">
                <Alert data-sentry-element="Alert" data-sentry-source-file="apps-section.tsx">
                    <Interpolate t={t} i18nKey="socCompliance" mapping={{
          link: <Link href="https://doist.typeform.com/certifications" />
        }} data-sentry-element="Interpolate" data-sentry-source-file="apps-section.tsx" />
                </Alert>
            </Container>
        </ProductUIThemeProvider>;
}